import type { ListResult } from "@checkone/contract/client";
import { SOURCE_LABEL, formatDay, shaPrefix } from "../lib/format";

type Source = "ATL_IT" | "ATL_ST";

/**
 * Both lists are membership lists: a vendor is on FBR's file or it is not. There is no suspended or
 * blacklisted state. The list itself is named by the column or heading the status sits under.
 */
const LABELS = { active: "Active", not_listed: "Not on the list" } as const;

const NOT_CHECKED: Record<string, string> = {
  no_identifier: "Needs identifier",
  strn_only: "STRN only: add NTN or CNIC",
  no_current_file: "No current FBR file",
  pending_first_check: "Checking…",
};

/**
 * One list status with the FBR file it came from: "Active" in ink, "Not on the list" in the warning
 * colour. A status is never shown without its file date; a vendor that could not be checked says why.
 */
export function StatusPill({
  source,
  result,
  compact = false,
}: {
  source: Source;
  result: ListResult;
  compact?: boolean;
}) {
  if (result.status === "not_checked") {
    const why = NOT_CHECKED[result.reason ?? ""] ?? "Not checked";
    return (
      <span className="status">
        <span className="pill neutral">{compact ? "Not checked" : why}</span>
        {compact && <span className="file-date">{why}</span>}
      </span>
    );
  }
  const tone = result.status === "active" ? "ok" : "warn";
  return (
    <span className="status">
      <span className={`pill ${tone}`}>{LABELS[result.status]}</span>
      {result.file && (
        <span className="file-date" title={`${SOURCE_LABEL[source]} file sha256 ${result.file.sha256}`}>
          FBR file {formatDay(result.file.published_on)}
          {!compact && (
            <>
              {" "}
              · <span className="mono">{shaPrefix(result.file.sha256)}</span>
            </>
          )}
        </span>
      )}
    </span>
  );
}
